import * as React from "react";
import { SermonItem } from "./interfaces";
import { setDebounce } from "../helpers/setDebounce";
import { FetchApi } from "../../FetchApi";
import { useParams } from "react-router-dom";
import { FormHelper, ReactiveForm } from "../helpers/ReactiveForm";
import { PreacherPage } from "../preachers/interfaces";
import { CategoryPage } from "../categories/interfaces";
import { Loading } from "../helpers/Loading";
import { BsEditor } from "../helpers/BsEditor";
import { requestHelper } from "../helpers/requestHelper";
import { decode } from "html-entities";

export const ShowSermon = () => {
  // Fix 1: Change null to undefined for proper TypeScript typing
  const [sermon, setSermon] = React.useState<SermonItem | undefined>(undefined);
  const [preachers, setPreachers] = React.useState<PreacherPage | undefined>(undefined);
  const [categories, setCategories] = React.useState<CategoryPage | undefined>(undefined);
  const [subCategories, setSubCategories] = React.useState<CategoryPage | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] =
    React.useState<string>("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] =
    React.useState<string>("");

  const retrievePreachers = (body = {}) => {
    setDebounce(
      () => {
        FetchApi.get<PreacherPage>({
          path: "preachers",
          body: { sorts: "-created_at", ...body },
        }).then((response) => {
          setPreachers(response);
        });
      },
      "preachers",
      500
    );
  };

  const retrieveCategories = (body = {}) => {
    FetchApi.get<CategoryPage>({
      path: "categories",
      body: { sorts: "-created_at", filters: "depth:0", ...body },
    }).then((response) => {
      setCategories(response);
    });
  };

  const retrieveSubCategories = (categoryId: string) => {
    if (!categoryId) {
      setSubCategories(undefined); // Fix 2: Use undefined instead of null
      return;
    }
    FetchApi.get<CategoryPage>({
      path: "categories",
      body: {
        sorts: "-created_at",
        filters: `parent_id:${categoryId}`,
        paginate: 1000000000000,
      },
    }).then((response) => {
      setSubCategories(response);
    });
  };

  const { id } = useParams<{ id: string }>();

  const retrieveSermon = (id: string) => {
    setDebounce(
      () => {
        FetchApi.get<SermonItem>({
          path: `sermons/${id}`,
        }).then((response) => {
          setSermon(response);
          // Set initial category values from sermon data
          if (response.data.category) {
            setSelectedCategoryId(response.data.category.id);
            // Load subcategories for the selected category
            retrieveSubCategories(response.data.category.id);
          }
          if (response.data.sub_category) {
            setSelectedSubCategoryId(response.data.sub_category.id);
          }
        });
      },
      "sermons",
      500
    );
  };

  React.useEffect(() => {
    retrievePreachers({ paginate: 1000000000000 });
    retrieveCategories({ paginate: 1000000000000 });
    retrieveSermon(id);
  }, [id]);

  React.useEffect(() => {
    if (selectedCategoryId && sermon) {
      // Only fetch subcategories if the category has changed from the initial value
      if (sermon.data.category?.id !== selectedCategoryId) {
        retrieveSubCategories(selectedCategoryId);
        setSelectedSubCategoryId("");
      }
    } else if (!selectedCategoryId) {
      setSubCategories(undefined); // Fix 3: Use undefined instead of null
      setSelectedSubCategoryId("");
    }
  }, [selectedCategoryId]);

  function updateSermon(event: FormHelper) {
    requestHelper({
      form: event,
      request: FetchApi.patch({
        path: `sermons/${id}`,
        body: event.value,
      }).then((m) => {
        retrieveSermon(id);
        return m;
      }),
    });
  }

  return (
    <>
      <div className="d-flex p-2 p-md-4" style={{ minHeight: "100vh" }}>
        <div className="my-auto w-100">
          <div className="d-flex">
            <h2>تعديل خطبة</h2>
          </div>
          <Loading
            loading={sermon}
            render={() => (
              <>
                <ReactiveForm
                  onSubmit={updateSermon}
                  valueAs={"json"}
                  className="card my-3"
                >
                  <button className="btn btn-primary btn-lg btn-floating">
                    <i className="fas fa-save fa-fw" />
                  </button>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            name="title"
                            placeholder=""
                            defaultValue={sermon!.data.title}
                          />
                          <label>عنوان الخطبة</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <Loading
                          loading={preachers}
                          render={() => (
                            <>
                              <div className="form-floating">
                                <select
                                  className="form-control"
                                  name="preacher_id"
                                  defaultValue={sermon!.data.preacher.id}
                                >
                                  {preachers!.data.map((preacher) => (
                                    <option
                                      key={preacher.id}
                                      value={preacher.id}
                                    >
                                      {preacher.name}
                                    </option>
                                  ))}
                                </select>
                                <label>الخطيب</label>
                              </div>
                            </>
                          )}
                        />
                      </div>
                      <div className="col-md-6 my-3">
                        <Loading
                          loading={categories}
                          render={() => (
                            <>
                              <div className="form-floating">
                                <select
                                  className="form-select"
                                  name="category_id"
                                  placeholder=""
                                  value={selectedCategoryId}
                                  onChange={(e) =>
                                    setSelectedCategoryId(e.target.value)
                                  }
                                >
                                  <option value="">اختر التصنيف الرئيسي</option>
                                  {categories!.data.map((category) => (
                                    <option
                                      key={category.id}
                                      value={category.id}
                                    >
                                      {category.name}
                                    </option>
                                  ))}
                                </select>
                                <label>التصنيف الرئيسي</label>
                              </div>
                            </>
                          )}
                        />
                      </div>
                      <div className="col-md-6 my-3">
                        {selectedCategoryId && subCategories && (
                          <div className="form-floating">
                            <select
                              className="form-select"
                              name="sub_category_id"
                              placeholder=""
                              value={selectedSubCategoryId}
                              onChange={(e) =>
                                setSelectedSubCategoryId(e.target.value)
                              }
                            >
                              <option value="">اختر التصنيف الفرعي</option>
                              {subCategories.data.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                            <label>التصنيف الفرعي</label>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-12 my-3">
                      <div className="form-floating">
                        <BsEditor
                          name="body"
                          defaultValue={decode(sermon!.data.body)}
                        />
                        <label>المحتوى</label>
                      </div>
                    </div>
                    <div className="col-12 my-3">
                      <label className={"form-group p-3"}>
                        <input
                          type="checkbox"
                          name="is_summerized"
                          className="slidebox"
                          defaultChecked={sermon!.data.is_summerized}
                        />
                        خطبة مختصرة
                      </label>
                    </div>
                    <div className="col-12 my-3">
                      <label className={"form-group p-3"}>
                        <input
                          type="checkbox"
                          name="is_featured"
                          className="slidebox"
                          defaultChecked={sermon!.data.is_featured}
                        />
                        خطبة مميزة
                      </label>
                    </div>
                  </div>
                </ReactiveForm>
              </>
            )}
          />
        </div>
      </div>
    </>
  );
};